import { EngineContext } from '../../engine';
import { SCENES, UI } from '../constants';
import { MenuSelection } from '../state';
import { drawCenteredText } from '../../ui/text';
import { drawScroll, centerScroll } from '../../ui/scroll';
import { Scene, SceneManager } from './sceneManager';
import { GameServices } from '../services';
import { EnvironmentBackdrop } from '../environment';
import { ARENA } from '../constants';
import { WOMBAT_FACTS } from '../facts';

const LOGO_GLYPHS: Record<string, string[]> = {
  W: ['1...1', '1...1', '1.1.1', '1.1.1', '1...1', '1...1'],
  O: ['.111.', '1...1', '1...1', '1...1', '1...1', '.111.'],
  M: ['1...1', '11.11', '1.1.1', '1...1', '1...1', '1...1'],
  B: ['1111.', '1...1', '1..1.', '1111.', '1...1', '1111.'],
  A: ['.111.', '1...1', '1...1', '11111', '1...1', '1...1'],
  T: ['11111', '..1..', '..1..', '..1..', '..1..', '..1..'],
  C: ['.1111', '1....', '1....', '1....', '1....', '.1111'],
  ' ': ['.....', '.....', '.....', '.....', '.....', '.....'],
};

export class MenuScene implements Scene {
  key = SCENES.MENU;
  private selection: MenuSelection = {
    options: ['Start', 'Exit'],
    index: 0,
  };
  private farewell = false;
  private navCooldown = 0;
  private forest = new EnvironmentBackdrop(Date.now(), 'forest');
  private confirmCooldown = 0;
  private awaitingInputRelease = false;
  private enteringNickname = false;
  private nicknameBuffer = '';
  private nicknameFact = WOMBAT_FACTS[0];

  constructor(private manager: SceneManager, private services: GameServices) {}

  init(): void {
    this.selection.index = 0;
    this.farewell = false;
    this.navCooldown = 0;
    this.confirmCooldown = 0.25;
    this.awaitingInputRelease = true;
    this.enteringNickname = false;
    this.nicknameBuffer = this.services.ranking.getNickname();
    this.nicknameFact = this.pickNicknameFact();
    this.services.audio.playBackground('forest');
  }

  update(context: EngineContext): void {
    if (this.enteringNickname) {
      this.handleNicknameEntry(context);
      return;
    }

    const dt = context.delta;
    this.navCooldown = Math.max(0, this.navCooldown - dt);
    this.confirmCooldown = Math.max(0, this.confirmCooldown - dt);

    if (this.awaitingInputRelease) {
      const heldKeys =
        context.input.isHeld(' ') ||
        context.input.isHeld('enter') ||
        context.input.isHeld('s') ||
        context.input.isHeld('arrowdown') ||
        context.input.isHeld('w') ||
        context.input.isHeld('arrowup');
      if (heldKeys) {
        return;
      }
      this.awaitingInputRelease = false;
    }

    const up =
      context.input.isPressed('w') ||
      context.input.isPressed('arrowup');
    const down =
      context.input.isPressed('s') ||
      context.input.isPressed('arrowdown');
    const rawConfirm =
      context.input.isPressed('enter') ||
      context.input.isPressed(' ');
    const confirm = rawConfirm && this.confirmCooldown <= 0;
    const cancel = context.input.isPressed('escape');

    if (this.farewell && (confirm || cancel)) {
      this.farewell = false;
      return;
    }

    if (up && this.navCooldown <= 0) {
      this.selection.index =
        (this.selection.index - 1 + this.selection.options.length) %
        this.selection.options.length;
      this.navCooldown = 0.18;
    }
    if (down && this.navCooldown <= 0) {
      this.selection.index =
        (this.selection.index + 1) % this.selection.options.length;
      this.navCooldown = 0.18;
    }

    if (confirm) {
      const choice = this.selection.options[this.selection.index];
      if (choice === 'Start') {
        this.beginNicknameEntry(context);
        this.confirmCooldown = 0.25;
        return;
      } else {
        this.farewell = true;
        this.services.audio.stopBackground();
      }
      this.confirmCooldown = 0.25;
    }
  }

  render(context: EngineContext): void {
    const { ctx, time } = context;
    this.forest.render(ctx, time);

    this.drawLogo(ctx, time);

    if (this.enteringNickname) {
      this.drawNicknameBoard(ctx, time);
      return;
    }

    if (this.farewell) {
      drawCenteredText(ctx, 'See you in the forest!', 102, UI.largeFont, UI.colorAccent);
      drawCenteredText(ctx, 'Press confirm to return', 124, UI.font, UI.colorPrimary);
      return;
    }

    this.selection.options.forEach((option, index) => {
      const y = 96 + index * 22;
      const selected = index === this.selection.index;
      drawCenteredText(
        ctx,
        `${selected ? '>' : ' '} ${option}`,
        y,
        UI.font,
        selected ? UI.colorAccent : UI.colorPrimary,
      );
    });

    this.drawControlHints(ctx);
  }

  private drawLogo(ctx: CanvasRenderingContext2D, time: number): void {
    const lines = ['WOMBAT', 'COMBAT'];
    const scale = 4;
    const glyphHeight = LOGO_GLYPHS.W.length;
    const letterSpacing = 2;
    const lineSpacing = 3;

    const measureLine = (text: string): number =>
      text.split('').reduce((acc, char, index) => {
        const glyph = LOGO_GLYPHS[char] ?? LOGO_GLYPHS[' '];
        const width = glyph[0].length;
        return acc + width + (index < text.length - 1 ? letterSpacing : 0);
      }, 0) * scale;

    const totalHeight =
      lines.length * glyphHeight * scale + (lines.length - 1) * (glyphHeight + lineSpacing) * scale;
    let y = 46 - totalHeight / 2;
    const pulse = 0.05 * Math.sin(time * 6);

    lines.forEach((line, lineIndex) => {
      const lineWidth = measureLine(line);
      let x = ARENA.centerX - lineWidth / 2;
      const wobble = Math.sin(time * 3 + lineIndex) * 2;
      x += wobble;
      line.split('').forEach((char, charIndex) => {
        const glyph = LOGO_GLYPHS[char] ?? LOGO_GLYPHS[' '];
        glyph.forEach((row, rowIndex) => {
          row.split('').forEach((cell, colIndex) => {
            if (cell !== '1') {
              return;
            }
            const pixelX = x + colIndex * scale;
            const pixelY = y + rowIndex * scale;
            ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
            ctx.fillRect(pixelX + 1.4, pixelY + 1.4, scale, scale);
            const gradient = ctx.createLinearGradient(pixelX, pixelY, pixelX, pixelY + scale);
            gradient.addColorStop(0, `rgba(255, 133, 243, ${0.9 + pulse})`);
            gradient.addColorStop(1, `rgba(111, 255, 255, ${0.85 + pulse})`);
            ctx.fillStyle = gradient;
            ctx.fillRect(pixelX, pixelY, scale, scale);
          });
        });
        x += (glyph[0].length + letterSpacing) * scale;
        if (charIndex === 2) {
          x += scale;
        }
      });
      y += (glyphHeight + lineSpacing) * scale;
    });
  }

  private beginNicknameEntry(context: EngineContext): void {
    this.enteringNickname = true;
    this.nicknameBuffer = '';
    this.nicknameFact = this.pickNicknameFact();
    context.input.consumeTextInput();
  }

  private handleNicknameEntry(context: EngineContext): void {
    const typed = context.input.consumeTextInput();
    typed.forEach((key) => this.processNicknameInput(key));
    if (context.input.isPressed('escape')) {
      this.enteringNickname = false;
      this.nicknameBuffer = this.services.ranking.getNickname();
      return;
    }
    if (this.nicknameBuffer.length > 0 && context.input.isPressed(' ')) {
      this.commitNickname(context);
    }
  }

  private processNicknameInput(key: string): void {
    if (key === 'Backspace') {
      this.nicknameBuffer = this.nicknameBuffer.slice(0, -1);
      return;
    }
    if (key === 'Enter') {
      return;
    }
    if (key.length !== 1) {
      return;
    }
    if (/^[a-z0-9-]$/i.test(key) && this.nicknameBuffer.length < 16) {
      this.nicknameBuffer += key.toUpperCase();
    }
  }

  private commitNickname(context: EngineContext): void {
    const sanitized = this.cleanNickname(this.nicknameBuffer);
    if (!sanitized) {
      return;
    }
    this.services.ranking.setNickname(sanitized);
    this.enteringNickname = false;
    this.nicknameBuffer = sanitized;
    this.services.audio.playBackground('forest');
    this.manager.transition(SCENES.COMBAT, {
      level: 1,
      resetSuper: true,
      playerName: sanitized,
    });
  }

  private cleanNickname(raw: string): string {
    const trimmed = (raw ?? '').replace(/[^a-z0-9 ]/gi, '').trim().slice(0, 16);
    return trimmed || 'Anonymous';
  }

  private pickNicknameFact(): string {
    if (!WOMBAT_FACTS.length) {
      return 'Wombats dig spiral burrows to surprise intruders.';
    }
    const index = Math.floor(Math.random() * WOMBAT_FACTS.length);
    return WOMBAT_FACTS[index];
  }

  private drawNicknameBoard(ctx: CanvasRenderingContext2D, time: number): void {
    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
    ctx.fillRect(0, 0, ARENA.width, ARENA.height);

    const scrollWidth = 236;
    const scrollHeight = 160;
    const { x, y } = centerScroll(scrollWidth, scrollHeight);
    drawScroll(ctx, { x, y, width: scrollWidth, height: scrollHeight });

    ctx.textAlign = 'center';
    ctx.fillStyle = '#1f3b2c';
    ctx.font = '9px "Press Start 2P", monospace';
    ctx.fillText('Enter your username', ARENA.centerX, y + 24);

    ctx.font = '7px "Press Start 2P", monospace';
    ctx.fillStyle = '#4a3015';
    ctx.fillText('Here is a wombat fact for inspiration:', ARENA.centerX, y + 38);

    const factLines = this.wrapNicknameFact(ctx, this.nicknameFact, scrollWidth - 36);
    let cursorY = y + 54;
    factLines.forEach((line) => {
      ctx.fillText(line, ARENA.centerX, cursorY);
      cursorY += 12;
    });

    cursorY += 6;
    ctx.fillStyle = '#1a3d2c';
    ctx.font = '8px "Press Start 2P", monospace';
    ctx.fillText('Letters & digits, "-" for gaps', ARENA.centerX, cursorY);
    cursorY += 14;

    const boxWidth = scrollWidth - 48;
    const boxHeight = 20;
    const boxX = ARENA.centerX - boxWidth / 2;
    const boxY = cursorY - 12;
    ctx.fillStyle = 'rgba(14, 33, 24, 0.95)';
    ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
    ctx.strokeStyle = '#4f8f6a';
    ctx.lineWidth = 1;
    ctx.strokeRect(boxX + 0.5, boxY + 0.5, boxWidth - 1, boxHeight - 1);

    const caretVisible = Math.floor(time * 2) % 2 === 0;
    ctx.textAlign = 'left';
    ctx.fillStyle = '#c8ffe2';
    ctx.font = '9px "Press Start 2P", monospace';
    ctx.fillText(
      `${this.nicknameBuffer}${caretVisible ? '_' : ''}`,
      boxX + 8,
      boxY + boxHeight - 5,
    );

    ctx.textAlign = 'center';
    ctx.font = '7px "Press Start 2P", monospace';
    ctx.fillStyle = '#c8ffe2';
    if (this.nicknameBuffer.length === 0) {
      ctx.fillText('Type your codename to enable start', ARENA.centerX, y + scrollHeight - 26);
    } else {
      ctx.fillText('Press SPACE to begin', ARENA.centerX, y + scrollHeight - 26);
    }
    ctx.fillText('Esc = cancel', ARENA.centerX, y + scrollHeight - 12);

    ctx.restore();
  }

  private wrapNicknameFact(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const words = text.split(/\s+/);
    const lines: string[] = [];
    let current = '';
    words.forEach((word) => {
      const candidate = current ? `${current} ${word}` : word;
      if (ctx.measureText(candidate).width <= maxWidth || !current) {
        current = candidate;
      } else {
        lines.push(current);
        current = word;
      }
    });
    if (current) {
      lines.push(current);
    }
    return lines;
  }

  private drawControlHints(ctx: CanvasRenderingContext2D): void {
    const lines: Array<
      Array<
        | { type: 'label'; text: string }
        | { type: 'keys'; keys: string[] }
      >
    > = [
      [
        { type: 'label', text: 'Move:' },
        { type: 'keys', keys: ['W', 'A', 'S', 'D'] },
      ],
      [
        { type: 'label', text: 'Punch:' },
        { type: 'keys', keys: ['P'] },
        { type: 'label', text: 'Kick:' },
        { type: 'keys', keys: ['K'] },
      ],
      [
        { type: 'label', text: 'Jump:' },
        { type: 'keys', keys: ['↑'] },
        { type: 'label', text: 'SUPPA ATAK:' },
        { type: 'keys', keys: ['Space'] },
      ],
    ];

    ctx.save();
    ctx.font = '6px "Press Start 2P", monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    const startY = 146;
    const lineSpacing = 9;
    const itemGap = 4;
    const keyGap = 2;
    lines.forEach((segments, index) => {
      const baseline = startY + index * lineSpacing;
      const lineWidth = segments.reduce((acc, segment, segmentIndex) => {
        if (segment.type === 'label') {
          return acc + ctx.measureText(segment.text).width + (segmentIndex > 0 ? itemGap : 0);
        }
        const keysWidth = segment.keys.reduce((sum, key, keyIndex) => {
          const width = this.measureKeycapWidth(ctx, key);
          const spacing = keyIndex > 0 ? keyGap : 0;
          return sum + width + spacing;
        }, 0);
        return acc + keysWidth + (segmentIndex > 0 ? itemGap : 0);
      }, 0);

      let x = ARENA.width / 2 - lineWidth / 2;
      segments.forEach((segment, segmentIndex) => {
        if (segmentIndex > 0) {
          x += itemGap;
        }
        if (segment.type === 'label') {
          ctx.fillStyle = '#ffe066';
          ctx.fillText(segment.text, x, baseline);
          x += ctx.measureText(segment.text).width;
        } else {
          segment.keys.forEach((key, keyIndex) => {
            if (keyIndex > 0) {
              x += keyGap;
            }
            this.drawMiniKeycap(ctx, key, x, baseline - 6);
            x += this.measureKeycapWidth(ctx, key);
          });
        }
      });
    });
    ctx.restore();
  }

  private drawMiniKeycap(ctx: CanvasRenderingContext2D, label: string, x: number, y: number): void {
    ctx.save();
    const width = this.measureKeycapWidth(ctx, label);
    ctx.fillStyle = '#fafafa';
    ctx.fillRect(x, y, width, 10);
    ctx.strokeStyle = '#0c140f';
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, width - 1, 9);
    ctx.fillStyle = '#0c140f';
    ctx.font = '6px "Press Start 2P", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, x + width / 2, y + 5);
    ctx.restore();
  }

  private measureKeycapWidth(ctx: CanvasRenderingContext2D, label: string): number {
    const textWidth = ctx.measureText(label).width;
    return Math.max(10, Math.ceil(textWidth + 6));
  }

}
